import p5 from "p5";
import { TWO_PI, PHI, SQR_2, GA, SGR } from "./local_mods/math_constants.js";
import { rothko_palettes } from "./local_mods/rothko_palettes.js";
import { project_description_for_console } from "./local_mods/proj_desc.js";
import { gen_search } from "./gen_search.js";
import { get_ctr } from "./translate.js";
import {
  frand_rng,
  arr_pick,
  fib_num,
  render_dims, // K
  fx_shuffle_arr,
  calculateAspectRatioFit,
  bias_arr_pick,
  lerp,
} from "./local_mods/util.js";

let myp5 = new p5((p55) => {
  console.clear();

  let screen_pg;
  let c;

  const config = {
    author: "sspboyd",
    sketch_name: "binary_trees",
    fxhash,
  };
  // I
  const set_scene = function () {
    const OPRs = [PHI, SGR, GA];
    const scene_OPR = bias_arr_pick(OPRs, 1.11);
    const OPR = scene_OPR.nat;
    const i_OPR = scene_OPR.inv;

    const final_num_tree_fact = bias_arr_pick([14, 11, 13, 12], 1.11);
    const final_num_trees = fib_num(final_num_tree_fact);

    // bias the selection closer to 0 than final_num_tree_fact
    const init_num_tree_fact = Math.floor(
      lerp(0, final_num_tree_fact, frand_rng(1), Math.pow(OPR, 2))
    );
    const init_num_trees = fib_num(init_num_tree_fact);

    const num_trees = {
      init: init_num_trees,
      final: final_num_trees, // R
    };

    let found_count = 0;

    const sspace = {
      min: 0,
      max_fact: bias_arr_pick([29, 11, 18], 1.11),
    };

    const init_rot = TWO_PI * Math.pow(i_OPR, Math.floor(frand_rng(5)));

    const pn_seed = Math.floor(frand_rng(4294967296));
    const pn_shift_incr = 0.07;

    const pal_idx_num = bias_arr_pick([21, 19, 16, 2, 3, 4, 6], 1.18); // the arr numbers are based on the idx in the colour palettes file
    const pal_idx = rothko_palettes.findIndex((p) => p.idx === pal_idx_num);
    const pal = rothko_palettes[pal_idx];

    const bkg_clr_idx = frand_rng(1) < i_OPR ? 0 : pal.clrs.length - 1;
    const bkg_clr = p55.color(pal.clrs[bkg_clr_idx]);
    pal.clrs.splice(bkg_clr_idx, 1); // remove this bkg clr from the list of colours used for arcs

    const aspect = {};
    aspect.name = "Square";
    aspect.layout = "square";
    aspect.ratio = { w: 1, h: 1 };

    const aspect_ratio_fact = frand_rng(1);
    if (aspect_ratio_fact < 1 - i_OPR) {
      aspect.name = "Landscape";
      aspect.layout = "land";
      aspect.ratio.w = SQR_2;
      aspect.ratio.h = 1;
    } else if (aspect_ratio_fact > i_OPR) {
      aspect.name = "Portrait";
      aspect.layout = "port";
      aspect.ratio.w = 1;
      aspect.ratio.h = SQR_2;
    }
    aspect.dims = render_dims[aspect.layout];

    const ctr = {}; // will hold x,y positions for the centre/spawn pt of the trees

    return {
      OPR,
      i_OPR,
      scene_OPR,
      aspect,
      bkg_clr,
      ctr,
      rothko_palettes,
      config,
      found_count,
      init_rot,
      pn_seed,
      pn_shift_incr,
      num_trees,
      pal,
      sspace,
    };
  };

  const scene = set_scene();
  window.$fxhashFeatures = {
    "The Organizing Principle Ratio": scene.scene_OPR.title,
    "Aspect Ratio": scene.aspect.name,
    "Colour Palette": scene.pal.title,
    "Maximum Search Space": fib_num(scene.sspace.max_fact),
    "Number of Search Trees": scene.num_trees.final,
  };

  const searches = [];

  const render = function (curr_dims, pg) {
    pg.clear();
    pg.noStroke(); // H
    pg.background(scene.bkg_clr);

    if (searches.length < scene.num_trees.final) {
      scene.ctr = get_ctr(searches);
    }
    pg.push();
    pg.translate(
      curr_dims.x / 2 - scene.ctr.x * curr_dims.x,
      curr_dims.y / 2 - scene.ctr.y * curr_dims.y
    );
    searches.forEach((s, i) => {
      s.update(curr_dims);
      s.render(curr_dims, pg);
    });
    pg.pop();
    return pg;
  };

  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  p55.setup = () => {
    p55.pixelDensity(1); //>>::""{{{&}}}
    p55.frameRate(30);
    p55.noiseSeed(scene.pn_seed);

    let screen_dims = calculateAspectRatioFit(
      scene.aspect.ratio.w,
      scene.aspect.ratio.h,
      window.innerWidth,
      window.innerHeight
    );

    screen_dims.x = 2 * Math.round(screen_dims.x / 2);
    screen_dims.y = 2 * Math.round(screen_dims.y / 2);

    render_dims.screen = screen_dims;
    scene.aspect.dims.screen = screen_dims;

    screen_pg = p55.createGraphics(
      scene.aspect.dims.screen.x,
      scene.aspect.dims.screen.y
    );
    c = p55.createCanvas(screen_pg.width, screen_pg.height);
    if (config.blend_mode_active) {
      screen_pg.blendMode(config.blend_mode);
      c.blendMode(config.blend_mode);
    }

    screen_pg.strokeCap(p55.PROJECT);
    p55.strokeCap(p55.PROJECT);

    for (let i = 0; i < scene.num_trees.init; i++) {
      searches.push(gen_search(p55, scene, searches));
    }
    project_description_for_console();
  };

  p55.draw = () => {
    p55.image(render(scene.aspect.dims.screen, screen_pg), 0, 0);
    if (scene.found_count > scene.num_trees.final - 1) {
      console.log(
        `Secret numbers found for all ${
          scene.num_trees.final
        } trees.\n Time to complete: ${(
          window.performance.now() / 1000
        ).toFixed(1)} seconds.`
      );
      fxpreview();
      console.log(`Scene Object:`);
      console.dir(scene);
      p55.noLoop();
    }
  }; // S

  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  p55.keyTyped = () => {
    keyboard_input(p55, p55.key, render_dims);
  };

  /**
   * This function handles keyboard input
   * @param {*} p55
   * @param {*} key
   * @param {*} render_dims
   */
  const keyboard_input = function (p55, key, render_dims) {
    const ca_dims = scene.aspect.dims; // ca_dims = current aspect dimensions
    switch (key) {
      case "T":
      case "t":
        console.group(
          `Saving thumbnail at ${ca_dims.thumb.x} x ${ca_dims.thumb.y}`
        );
        export_img(p55, ca_dims.thumb);
        console.groupEnd();
        break;
      case "I":
      case "i":
        console.group(
          `Saving thumbnail at ${ca_dims.insta.x} x ${ca_dims.insta.y} for Instagram (1:1).`
        );
        export_img(p55, ca_dims.insta);
        console.groupEnd();
        break;
      case "S":
      case "s":
        console.group(
          `Saving screen size at ${ca_dims.screen.x} x ${ca_dims.screen.y}`
        );
        export_img(p55, ca_dims.screen);
        console.groupEnd();
        break;
      case "P":
      case "p":
        console.group(
          `Saving print (16" @ 300dpi) at ${ca_dims.print.x} x ${ca_dims.print.y}`
        );
        export_img(p55, ca_dims.print);
        console.groupEnd();
        break;
      case "M":
      case "m":
        console.group(
          `Saving at ${ca_dims.max.x} x ${ca_dims.max.y}. Really?? This is ridiculously large. What are you thinking??!?!?`
        );
        export_img(p55, ca_dims.max);
        console.groupEnd();
        break;
      case "H":
      case "h":
        console.group(
          `Saving at 4K on the vertial at ${ca_dims.hd4k.x} x ${ca_dims.hd4k.y}`
        );
        export_img(p55, ca_dims.hd4k);
        console.groupEnd();
        break;
      default:
        console.log(
          "Hmm. Your keyboard input is not recognized. Try using S, P, M or H to export to PNG."
        );
    }
  };

  /**
   * This function renders an image and saves it out
   * @param {*} p55
   * @param {*} dims
   */
  const export_img = function (p55, dims) {
    const export_pg = p55.createGraphics(dims.x, dims.y);
    export_pg.background(scene.bkg_clr);
    if (config.blend_mode_active) {
      export_pg.blendMode(config.blend);
    }
    export_pg.strokeCap(p55.PROJECT);

    const sk_name = config.sketch_name;
    const res = `${dims.x}x${dims.y}`;

    // generate date string: YYYY-MM-DD-HH-MM-SS
    const dt = new Date();
    const datetime = `${dt.getFullYear().toString()}${(dt.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${dt.getDate().toString().padStart(2, "0")}${dt
      .getHours()
      .toString()
      .padStart(2, "0")}${dt.getMinutes().toString().padStart(2, "0")}${dt
      .getSeconds()
      .toString()
      .padStart(2, "0")}${dt.getMilliseconds().toString().padStart(3, "0")}`;
    const filename = `${sk_name}-by_${config.author}-${datetime}-${res}-${fxhash}`;
    console.log(`saving image to: ${filename}`);
    const output_img = render(dims, export_pg);
    output_img.save(filename);
  };
});
